import { isEqual, sortBy, sortedUniqBy } from 'lodash-es';
import { clamp } from '@cf-shared/shared-util';
import { Equipment, EquipmentElem_Fixed, EquipmentElem_Stack, EquipmentType, countStepValues } from '@cf-shared/data-model/equipment';

/**
 * Dumbbells (`fixed`) and weight stacks (`stack`) are the same thing under two
 * names: a list of weights on the equipment, shown on the edit page as ranges
 * of "from / to / increment". They differ only in the key the list lives under
 * and in the name of each element's weight field. This file holds that shared
 * handling once, so the edit page does not carry a hand-copied block per kind.
 *
 * Plates are grouped differently (by equal weight, with a count) and are
 * deliberately not part of this.
 */

// one range of weights as the edit page shows it: first, last, and the step between them
export interface RangeWeightGroup {
  firstWeight_kg: number;
  lastWeight_kg: number;
  stepWeight_kg: number;
  description?: string;  // filled in to show in the UI
}

// describes one kind of range-grouped weight list: where it lives on the equipment and how its elements look
export interface RangeWeightKind<E> {
  equipmentType: EquipmentType.Fixed | EquipmentType.Stack;
  // true when the equipment object carries this kind's list key at all, even if empty
  isPresentOn(equipment: Equipment): boolean;
  getElements(equipment: Equipment): E[] | undefined;
  setElements(equipment: Equipment, elements: E[]): void;
  weightOf(element: E): number;
  makeElement(weight_kg: number): E;
}

type FixedElement = EquipmentElem_Fixed['fixed'][number];
type StackElement = EquipmentElem_Stack['stack'][number];

export const FIXED_WEIGHTS: RangeWeightKind<FixedElement> = {
  equipmentType: EquipmentType.Fixed,
  isPresentOn: equipment => 'fixed' in equipment,
  getElements: equipment => (equipment as EquipmentElem_Fixed).fixed,
  setElements: (equipment, elements) => { (equipment as EquipmentElem_Fixed).fixed = elements; },
  weightOf: element => element.weight_kg,
  makeElement: weight_kg => ({ weight_kg }),
};

export const STACK_WEIGHTS: RangeWeightKind<StackElement> = {
  equipmentType: EquipmentType.Stack,
  isPresentOn: equipment => 'stack' in equipment,
  getElements: equipment => (equipment as EquipmentElem_Stack).stack,
  setElements: (equipment, elements) => { (equipment as EquipmentElem_Stack).stack = elements; },
  weightOf: element => element.totalWeight_kg,
  makeElement: totalWeight_kg => ({ totalWeight_kg }),
};

/* True for a row the user typed with two different ends and no increment at all, such as
   from 10 to 20 with the increment box left empty. Such a row says nothing about the weights
   in between, so it stands for just its two ends - see expandRangeGroup, which is where that
   is decided. The count and the weights must always agree about which rows these are, so they
   both ask this rather than each spelling the condition out (df-082e8158951b).
   An unset weight makes both comparisons false, so a row nobody has filled in is not one. */
function isRangeWithNoIncrement(g: RangeWeightGroup): boolean {
  return g.lastWeight_kg > g.firstWeight_kg && !g.stepWeight_kg;
}

// Safely (!) calculate the number of steps after the first weight in a group - works around divisions by zero etc
export function getRangeGroupCount(g: RangeWeightGroup) {
  // if the group is one lone real weight (grouping gives it step 0), it has no further steps.
  // without this, 0 / 0 is NaN, clamp lets NaN through, and the weight is dropped on save (df-0f1e5b8e4b0c).
  // a row the user has just added and not filled in (all fields unset) must still produce no weight, hence the > 0
  if (g.firstWeight_kg > 0 && g.lastWeight_kg === g.firstWeight_kg) {
    return 0;
  }
  // such a row stands for just its two ends, so one step.
  // without this, dividing by the missing increment gives Infinity, clamp turns that into 100,
  // and the row becomes 101 copies of the first weight while the last one is lost (df-082e8158951b)
  if (isRangeWithNoIncrement(g)) {
    return 1;
  }
  return clamp(Math.round((g.lastWeight_kg - g.firstWeight_kg) / g.stepWeight_kg), 0, 100);
}

// sort the kind's list on the equipment in place, lightest first
export function sortRangeWeights<E>(kind: RangeWeightKind<E>, equipment: Equipment) {
  let elements = kind.getElements(equipment);
  if (elements && elements.length > 0) {
    elements.sort((a, b) => kind.weightOf(a) - kind.weightOf(b));
  }
}

// turn a list of weights into ranges for display
export function groupRangeWeights(weights: number[]): RangeWeightGroup[] {
  let similar = countStepValues(weights);
  return similar.map(s => ({ firstWeight_kg: s.firstValue, stepWeight_kg: s.stepSize, lastWeight_kg: s.firstValue + (s.numValues - 1) * s.stepSize }));
}

/* Turn ONE range into the weights it stands for. Every place that expands a range asks this,
   so the weights shown beside a row and the weights stored on save can never disagree. */
export function expandRangeGroup(g: RangeWeightGroup): number[] {
  // keep just the two weights the user typed rather than inventing a step between them
  if (isRangeWithNoIncrement(g)) {
    return [g.firstWeight_kg, g.lastWeight_kg];
  }
  let count = getRangeGroupCount(g);
  // a missing increment counts as 0, so a lone weight typed with from = to and no increment is kept rather than turned into NaN
  let step = g.stepWeight_kg || 0;
  let weights: number[] = [];
  for (let i = 0; i <= count; i++) {
    weights.push(g.firstWeight_kg + i * step);
  }
  return weights;
}

// turn ranges back into the list of weights they stand for
export function ungroupRangeWeights(groups: RangeWeightGroup[]): number[] {
  let weights: number[] = [];
  for (let g of groups) {
    weights.push(...expandRangeGroup(g));
  }
  return weights;
}

// drop placeholder weights (zero or unset) and repeated weights, and keep the list sorted, as the page does just before saving.
// two overlapping rows (9 to 14, then 14 to 18) both produce 14, and the repeat carries no meaning (#5860)
export function normalizeRangeWeightsForSave<E>(kind: RangeWeightKind<E>, equipment: Equipment) {
  let elements = kind.getElements(equipment);
  if (elements) {
    let kept = elements.filter(e => kind.weightOf(e) > 0);
    kind.setElements(equipment, sortedUniqBy(sortBy(kept, e => kind.weightOf(e)), e => kind.weightOf(e)));
  }
}

/* Check if the groups of one kind can be cleaned up:
  - if any group has invalid values
  - if any group does not end on a whole number of steps
  - if neighbouring groups overlap
  - if groups are not sorted
  */
export function rangeGroupsNeedCleanUp(groups: RangeWeightGroup[]): boolean {
  if (groups.length === 0) {
    return false;
  }
  // check for valid values in general - a group of one lone weight (first equal to last) is valid
  if (groups.some(g => !(g.firstWeight_kg > 0 && g.firstWeight_kg <= g.lastWeight_kg && g.stepWeight_kg >= 0))) {
    return true;
  }
  // check for integer number of steps
  if (groups.some(g => {
    let count = getRangeGroupCount(g);
    let different = Math.abs(g.lastWeight_kg - (g.firstWeight_kg + count * g.stepWeight_kg)) > 1e-4;
    return different;
  })) {
    return true;
  }
  let sorted: RangeWeightGroup[] = sortBy(groups, ['firstWeight_kg']);
  // check if any of the neighbors overlap
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].lastWeight_kg >= sorted[i + 1].firstWeight_kg) {
      return true;
    }
  }
  // if they are not in order, we need to clean up
  return !isEqual(groups, sorted);
}
